import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  MeasuringStrategy,
  DropAnimation,
  defaultDropAnimationSideEffects,
  rectIntersection,
  pointerWithin,
} from "@dnd-kit/core";
import type {
  DragStartEvent,
  DragEndEvent,
  UniqueIdentifier,
  Active,
  Over,
  MeasuringConfiguration,
} from "@dnd-kit/core";
import { useSortable, SortableContext } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import classNames from "classnames";

import { Page, Position } from "./Page";
import type { Props as PageProps } from "./Page";
import pageStyles from "./Page.module.css";
import styles from "./index.module.css";
import {
  canSortByDirection,
  getPageItemIndexById,
  shouldMergeByDirection,
} from "./utils";
import { PageItem } from "@/constants/workbench";
import IconFont from "@meitu/xdesign-icon-font";
import { Tooltip } from "antd";
import "antd/es/tooltip/style/css";
import { statTrack } from "@/utils/mtstat";
import { throttle } from "lodash";

interface Props {
  items: PageItem[];
  onMergeItems: (fromId: string, toId: string) => void;
  onRemoveItem: (id: string) => void;
  onAddItem: (id: string) => void;
  onCopyItem: (id: string) => void;
  onReorder: (
    items: PageItem[],
    activeIndex: number,
    overIndex: number
  ) => void;
  onMdChange?: (
    id: string,
    markdown: string,
    html: string,
    len: number
  ) => void;
  onMdBlur?: (id: string) => void;
  onInputChange?: (id: string, value: string, type: "title" | "end") => void;
  onInputBlur?: (id: string) => void;
}

const measuring: MeasuringConfiguration = {
  droppable: {
    strategy: MeasuringStrategy.Always,
  },
};

const dropAnimation: DropAnimation = {
  keyframes({ transform }) {
    return [
      { transform: CSS.Transform.toString(transform.initial) },
      {
        transform: CSS.Transform.toString({
          scaleX: 0.98,
          scaleY: 0.98,
          x: transform.final.x - 10,
          y: transform.final.y - 10,
        }),
      },
    ];
  },
  sideEffects: defaultDropAnimationSideEffects({
    className: {
      active: pageStyles.active,
    },
  }),
};

export function Pages({
  items,
  onMergeItems,
  onRemoveItem,
  onAddItem,
  onCopyItem,
  onReorder,
  onMdChange,
  onMdBlur,
  onInputChange,
  onInputBlur,
}: Props) {
  const [activeId, setActiveId] = useState<string | null>(null);

  const [mergeTargetId, setMergeTargetId] = useState<string | null>(null);
  const activeIndex =
    activeId != null ? getPageItemIndexById(items, activeId) : -1;
  // 是否可以排序
  const [canSort, setCanSort] = useState(false);
  // 鼠标点击位置
  const clickPosition = useRef<{
    x: number;
    y: number;
  } | null>(null);
  const isMouseDown = useRef(false);

  const sensors = useSensors(useSensor(PointerSensor));

  function mergeItems(fromId: string, toId: string) {
    onMergeItems(fromId, toId);
  }

  // 实时判断是否合并
  const throttledHandleDragOver = useMemo(
    () =>
      throttle(({ active, over }: { active: Active; over: Over | null }) => {
        // console.log("【clickPosition】：", clickPosition.current, over, active);
        if (!over || active.id === over.id) {
          setMergeTargetId(null);
          return;
        }

        const activeIndex = getPageItemIndexById(items, active.id as string);
        const overIndex = getPageItemIndexById(items, over.id as string);

        const overItem = items[overIndex];

        if (overItem.type === "封面" || overItem.type === "致谢") {
          setMergeTargetId(null);
          setCanSort(false);
          return;
        }
        // 先判断是否可以合并
        const isToTop = activeIndex > overIndex;
        if (isToTop) {
          const overTop = over.rect.top + over.rect.height * (2 / 3);
          // 只要鼠标还在over的下方3/2处，就是合并
          if (clickPosition.current?.y && clickPosition.current.y > overTop) {
            setMergeTargetId(over.id as string);
            setCanSort(false);
            return;
          } else {
            setMergeTargetId(null);
            setCanSort(true);
            return;
          }
        } else {
          const oveTop = over.rect.top + over.rect.height * (2 / 3);
          // 只要鼠标还在over的上方3/2处，就是合并
          if (clickPosition.current?.y && clickPosition.current.y < oveTop) {
            setMergeTargetId(over.id as string);
            setCanSort(false);
            return;
          } else {
            setMergeTargetId(null);
            setCanSort(true);
            return;
          }
        }

        // const shouldMerge = shouldMergeByDirection(
        //   active,
        //   over,
        //   activeIndex,
        //   overIndex
        // );
        // const canSort = canSortByDirection(
        //   active,
        //   over,
        //   activeIndex,
        //   overIndex
        // );
        // setMergeTargetId(shouldMerge ? (over.id as string) : null);
        // setCanSort(canSort);
      }, 100), // 节流间隔：100ms
    [items]
  );

  function handleDragStart({ active }: DragStartEvent) {
    setActiveId(active.id as string);
  }

  function handleDragCancel() {
    setActiveId(null);
    setMergeTargetId(null);
  }

  // 处理拖动结束时的逻辑
  function handleDragEnd({ active, over }: DragEndEvent) {
    if (!over || !active.id || active.id === over.id) {
      setActiveId(null);
      setMergeTargetId(null);
      return;
    }

    if (mergeTargetId === over.id) {
      mergeItems(active.id as string, over.id as string);
      setActiveId(null);
      setMergeTargetId(null);
      return;
    }

    const activeIndex = getPageItemIndexById(items, active.id as string);
    const overIndex = getPageItemIndexById(items, over.id as string);

    if (activeIndex !== -1 && overIndex !== -1 && activeIndex !== overIndex) {
      // setItems((items) => arrayMove(items, activeIndex, overIndex));
      onReorder(items, activeIndex, overIndex);
    }
    setCanSort(false);
    setActiveId(null);
    setMergeTargetId(null);
  }

  const handleMouseDown = (e: MouseEvent) => {
    isMouseDown.current = true;
    clickPosition.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseUp = () => {
    isMouseDown.current = false;
    clickPosition.current = null;
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isMouseDown.current) {
      clickPosition.current = { x: e.clientX, y: e.clientY };
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("mousemove", handleMouseMove);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  return (
    <DndContext
      onDragStart={handleDragStart}
      // onDragOver={handleDragOver}
      onDragMove={throttledHandleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      sensors={sensors}
      collisionDetection={pointerWithin}
      measuring={measuring}
    >
      <SortableContext items={items}>
        <ul className={styles.pages}>
          {items.map((item, index) => (
            <React.Fragment key={item.id}>
              <SortablePage
                id={item.id}
                item={item}
                index={items?.[0].type === "封面" ? index : index + 1}
                activeIndex={activeIndex}
                canSort={canSort}
                mergeTargetId={mergeTargetId}
                onRemove={() => onRemoveItem(item.id)}
                onAdd={() => onAddItem(item.id)}
                onCopy={() => onCopyItem(item.id)}
                onMdChange={(markdown, html, len) =>
                  onMdChange?.(item.id, markdown, html, len)
                }
                onMdBlur={() => onMdBlur?.(item.id)}
                onInputChange={(value, type) =>
                  onInputChange?.(item.id, value, type)
                }
                onInputBlur={() => onInputBlur?.(item.id)}
              />
              {/* 注意：按钮不属于 SortableContext 逻辑，只是 UI 插入 */}
              {index !== items.length - 1 && (
                <div className={styles.addButton} key={`button-${item.id}`}>
                  <div className={styles.addButtonInner}>
                    <Tooltip
                      title="添加卡片"
                      placement="top"
                      showArrow={false}
                      align={{ offset: [0, 6] }}
                    >
                      <div
                        className={styles.addButtonWrap}
                        onClick={() => {
                          statTrack("tools_click_ppt_home", {
                            click_type: "add_page_between",
                          });
                          onAddItem(item.id);
                        }}
                      >
                        <IconFont name="PlusBlack" fontSize={10} color="#fff" />
                      </div>
                    </Tooltip>
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
        </ul>
      </SortableContext>
      <DragOverlay dropAnimation={dropAnimation}>
        {activeId != null ? <PageOverlay id={activeId} items={items} /> : null}
      </DragOverlay>
    </DndContext>
  );
}

function PageOverlay({
  id,
  items,
  ...props
}: Omit<PageProps, "index"> & { items: PageItem[] }) {
  const activeItem = items.find((item) => item.id === id);

  return (
    <Page
      id={id}
      {...props}
      item={activeItem}
      clone
      insertPosition={undefined}
    />
  );
}

function SortablePage({
  id,
  activeIndex,
  mergeTargetId,
  canSort,
  item,
  ...props
}: PageProps & {
  activeIndex: number;
  mergeTargetId: UniqueIdentifier | null;
  canSort: boolean;
}) {
  const {
    attributes,
    listeners,
    index,
    isDragging,
    isSorting,
    over,
    active,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    id,
    animateLayoutChanges: always,
  });

  const isMergeTarget = id === mergeTargetId;

  const renderPage = () => {
    return (
      <Page
        ref={setNodeRef}
        id={id}
        item={item}
        active={isDragging}
        className={classNames({
          [pageStyles.mergeTarget]: isMergeTarget,
        })}
        style={{
          transition,
          transform: isSorting ? undefined : CSS.Translate.toString(transform),
        }}
        insertPosition={
          // 优先判断是否是合并目标
          mergeTargetId === id
            ? undefined
            : over?.id === id && canSort
            ? index > activeIndex
              ? Position.After
              : Position.Before
            : undefined
        }
        {...props}
        {...attributes}
        {...listeners}
      />
    );
  };

  return (
    <Tooltip title="合并页面" placement="left" open={isMergeTarget}>
      {renderPage()}
    </Tooltip>
  );
}

function always() {
  return true;
}

export default React.memo(Pages);
